import { Component, computed, effect, inject, input, OnInit, output, Signal, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from '@/shared/components/toast/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '../card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ContatoService } from '@/shared/components/contato/contato.service';
import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';
import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';
import { EmailResponse, EnderecoResponse, TelefoneResponse } from '@/core/api/sicom-api/registro/pessoa/contato/contato.model';
import { concatMap, finalize, firstValueFrom, forkJoin } from 'rxjs';
import { LocalidadesService } from '@/core/api/consulta-api/localidades/localidades.service';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { PessoaService as PessoaSessionService } from '@/shared/services/pessoa.service';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { PermissionService } from '@/core/permissions/permission.service';
import { MascaraUtil } from '@/shared/utils/mascara-util';
import { ContextoRotaRegistroService } from '@/shared/services/contexto-rota-registro.service';
import { CanDirective } from '@/core/permissions/can.directive';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
enum TipoTelefone {
    CELULAR = 'C',
    FIXO = 'F'
}

enum TipoEmail {
    PESSOAL = 'P',
    COMERCIAL = 'C',
    FEDERAL = 'F'
}

enum EnderecoTipo {
    RESIDENCIAL = 'R',
    COMERCIAL = 'C',
    OUTRO = 'O'
}

interface TelefoneRow {
    idEndereco: number;
    ddd: string;
    telefone: string;
    tipo: TipoTelefone;
}

interface EnderecoRow {
    id: number;
    logradouro: string;
    bairro: string;
    municipio: string;
    uf: string;
    cep: string;
    tipo: EnderecoTipo;
    telefones: string[];
}

interface EmailRow {
    email: string;
    tipo: TipoEmail;
}

/** Siglas das 27 unidades federativas, para validar o campo UF do endereço contra a lista oficial. */
const UFS_BRASIL = new Set(['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']);

/** Valida que o valor informado é uma UF brasileira existente (vazio é tratado por `required`). */
function ufValidaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const valor = String(control.value ?? '')
            .trim()
            .toUpperCase();
        if (!valor) return null;
        return UFS_BRASIL.has(valor) ? null : { ufInvalida: true };
    };
}

@Component({
    selector: 'app-contato',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        CardModule,
        InputTextModule,
        InputMaskModule,
        ButtonModule,
        TableModule,
        SelectModule,
        DialogModule,
        ConfirmDialogModule,
        Toast,
        CardContent,
        CardContainer,
        CustomTableComponent,
        ProgressSpinner,
        TooltipModule,
        TagModule,
        CanDirective
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './contato.html',
    styleUrl: './contato.scss'
})
export class Contato implements OnInit {
    pessoa = input<Pessoa>();
    uf = input<string | null>(null);
    modoSomenteLeitura = input(false);
    iniciarComoCard = input(false);
    usarEndpointsDelegacia = input(false);
    /** Role da ação de escrita por módulo (contato é funcionalidade de cada módulo: medico.contato.*, prestador.contato.*, orgao.contato.*). Sem valor → sem gate de role. */
    roleEditarContato = input<string | undefined>(undefined);
    roleExcluirContato = input<string | undefined>(undefined);
    dadosAtualizados = output<void>();

    telefones = signal<TelefoneRow[]>([]);
    enderecos = signal<EnderecoRow[]>([]);
    emails = signal<EmailRow[]>([]);
    private fallbackPessoa = signal<Pessoa | null>(null);
    private componenteInicializado = signal(false);
    private permissaoEscritaContato = signal(true);
    private cicloPermissaoEscritaContato = 0;
    podeExibirAcoesContato = computed(() => !this.modoSomenteLeitura() && this.permissaoEscritaContato());
    protected ufContato(): string | undefined {
        return this.pessoa?.()?.uf ?? this.uf() ?? undefined;
    }
    podeEditarContato = computed(() => {
        if (!this.podeExibirAcoesContato()) return false;
        const role = this.roleEditarContato();
        return role ? this.permissionService.can(role, { uf: this.ufContato() }) : true;
    });
    podeExcluirContato = computed(() => {
        if (!this.podeExibirAcoesContato()) return false;
        const role = this.roleExcluirContato();
        return role ? this.permissionService.can(role, { uf: this.ufContato() }) : true;
    });

    loadingEndereco = false;
    loadingEmail = false;

    municipios = signal<MunicipioResponse[]>([]);

    private readonly fb = inject(FormBuilder);
    readonly enderecoForm = this.fb.nonNullable.group({
        cep: ['', Validators.required],
        logradouro: ['', [Validators.required, Validators.maxLength(70)]],
        numero: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.maxLength(10)]],
        complemento: ['', Validators.maxLength(30)],
        bairro: ['', [Validators.required, Validators.maxLength(30)]],
        municipio: this.fb.control<number | null>(null, Validators.required),
        uf: ['', [Validators.required, ufValidaValidator()]],
        tipo: ['R', Validators.required]
    });
    readonly editarEnderecoForm = this.fb.group({
        id: this.fb.control<string | number | null>(null),
        cep: this.fb.nonNullable.control('', Validators.required),
        logradouro: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(70)]),
        numero: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/^\d+$/), Validators.maxLength(10)]),
        complemento: this.fb.nonNullable.control('', Validators.maxLength(30)),
        bairro: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(30)]),
        municipio: this.fb.control<number | null>(null, Validators.required),
        uf: this.fb.nonNullable.control('', [Validators.required, ufValidaValidator()]),
        tipo: this.fb.nonNullable.control('R', Validators.required),
        telefones: this.fb.array<FormGroup>([])
    });
    readonly emailForm = this.fb.nonNullable.group({
        email: ['', [Validators.required, Validators.email]],
        tipo: ['P', Validators.required]
    });

    // Dialog Endereço
    displayEnderecoDialog = false;
    tiposEndereco = [
        { label: 'Residencial', value: 'R' },
        { label: 'Comercial', value: 'C' },
        { label: 'Outro', value: 'O' }
    ];

    // Dialog Editar Endereço/Telefones
    displayEditarEnderecoDialog = false;
    tiposTelefone = [
        { label: 'Celular', value: 'C' },
        { label: 'Fixo', value: 'F' }
    ];
    enderecoSelecionadoId: number | null = null;
    isEditEnderecoVisible = signal(false);

    // Dialog Email
    displayEmailDialog = false;
    emailOriginal: string | null = null;
    tiposEmail = [
        { label: 'Pessoal', value: 'P' },
        { label: 'Comercial', value: 'C' },
        { label: 'Federal', value: 'F' }
    ];

    emailsFormatados = computed(() => {
        return this.emails().map((e) => {
            const tipoObj = this.tiposEmail.find((t) => t.value === e.tipo);
            return {
                ...e,
                tipoDescricao: tipoObj ? tipoObj.label : e.tipo
            };
        });
    });

    enderecosFormatados = computed(() => {
        return this.enderecos().map((e) => ({
            ...e,
            municipioNome: e.municipio || ''
        }));
    });

    enderecosTableMetadata: Signal<TableMetadata> = computed(() => {
        const acoesEditaveis: Record<string, unknown> = {};
        if (this.podeEditarContato()) {
            acoesEditaveis['editar'] = { label: 'Editar', icon: 'pi pi-pencil', color: 'primary', command: (item: any) => this.abrirEditarEndereco(item) };
        }
        if (this.podeExcluirContato()) {
            acoesEditaveis['excluir'] = { label: 'Excluir', icon: 'pi pi-trash', color: 'danger', command: (item: any) => void this.confirmarExclusaoEndereco(item) };
        }
        const actions = this.modoSomenteLeitura()
            ? { editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'primary', command: (item: any) => this.editarEnderecoSomenteVisualizacao(item) } }
            : Object.keys(acoesEditaveis).length
              ? acoesEditaveis
              : undefined;

        return {
            headers: {
                logradouro: { type: 'text', value: 'Logradouro', hidden: false, default: 'N/A' },
                bairro: { type: 'text', value: 'Bairro', hidden: false, default: 'N/A' },
                municipioNome: { type: 'text', value: 'Município', hidden: false, default: 'N/A' },
                cep: { type: 'text', value: 'CEP', hidden: false, default: 'N/A' }
            },
            pillsField: {
                key: 'telefones',
                title: 'Telefones Relacionados',
                color: 'primary'
            },
            title: 'Logradouro',
            tag: '',
            actions
        } as TableMetadata;
    });

    private editarEnderecoSomenteVisualizacao(_item: EnderecoRow): void {
        // Placeholder visual para manter o padrão de CRUD em modo leitura.
    }

    enderecosColumnOrder = ['logradouro', 'bairro', 'municipioNome', 'cep'];

    emailTableMetadata: Signal<TableMetadata> = computed(() => {
        const acoes: Record<string, unknown> = {};
        if (this.podeEditarContato()) {
            acoes['editar'] = { label: 'Editar', icon: 'pi pi-pencil', color: 'primary', command: (item: any) => this.showEmailDialog(item) };
        }
        if (this.podeExcluirContato()) {
            acoes['excluir'] = { label: 'Excluir', icon: 'pi pi-trash', color: 'danger', command: (item: any) => void this.confirmarExclusaoEmail(item) };
        }
        const actions = Object.keys(acoes).length ? acoes : undefined;

        return {
            headers: {
                email: { type: 'text', value: 'Email', hidden: false, default: 'N/A' },
                tipoDescricao: { type: 'text', value: 'Tipo', hidden: false, default: 'N/A' }
            },
            title: 'Email',
            tag: '',
            actions
        } as TableMetadata;
    });

    emailsColumnOrder = ['email', 'tipoDescricao'];
    initialViewMode = computed<'table' | 'card'>(() => {
        if (this.iniciarComoCard()) {
            return 'card';
        }

        return this.contextoRotaRegistroService.ehSistemaRegistro() ? 'table' : 'card';
    });

    private readonly permissaoService = inject(PermissaoService);
    private readonly permissionService = inject(PermissionService);

    obterTipoEnderecoExibicao(tipo: unknown): string {
        const tipoNormalizado = (tipo || '').toString().trim().toUpperCase();
        return this.tiposEndereco.find((item) => item.value === tipoNormalizado)?.label || 'N/A';
    }

    obterTipoEnderecoColor(tipo: unknown): string {
        const coresPorTipo: Record<string, string> = {
            [EnderecoTipo.RESIDENCIAL]: '#2563eb',
            [EnderecoTipo.COMERCIAL]: '#059669',
            [EnderecoTipo.OUTRO]: '#64748b'
        };
        const tipoNormalizado = (tipo || '').toString().trim().toUpperCase();
        return coresPorTipo[tipoNormalizado] || coresPorTipo[EnderecoTipo.OUTRO];
    }

    obterLogradouroExibicao(endereco: Partial<EnderecoRow> | null | undefined): string {
        return (endereco?.logradouro || '').trim() || 'N/A';
    }

    obterTipoEmailColor(tipo: unknown): string {
        const coresPorTipo: Record<string, string> = {
            [TipoEmail.PESSOAL]: '#7c3aed',
            [TipoEmail.COMERCIAL]: '#0891b2',
            [TipoEmail.FEDERAL]: '#b45309'
        };
        const tipoNormalizado = (tipo || '').toString().trim().toUpperCase();
        return coresPorTipo[tipoNormalizado] || '#64748b';
    }

    obterEmailExibicao(email: Partial<EmailRow> | null | undefined): string {
        return (email?.email || '').trim() || 'N/A';
    }

    constructor(
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private contatoService: ContatoService,
        private localidadesService: LocalidadesService,
        private municipioService: MunicipioService,
        private pessoaSessionService: PessoaSessionService,
        private contextoRotaRegistroService: ContextoRotaRegistroService
    ) {
        const stored = this.pessoaSessionService.getPessoa() ?? this.pessoaSessionService.getPessoaReference();
        if (stored) {
            this.fallbackPessoa.set({ id: Number(stored.id), uf: (stored as any).uf });
        }

        effect(() => {
            if (!this.componenteInicializado()) {
                return;
            }

            this.pessoa();
            this.uf();
            void this.sincronizarPermissaoECarregarContatos();
        });
    }

    ngOnInit(): void {
        this.initEnderecoForm();
        this.initEditarEnderecoForm();
        this.initEmailForm();
        this.carregarMunicipios();
        this.componenteInicializado.set(true);
    }

    carregandoAba(): boolean {
        return this.loadingEndereco || this.loadingEmail;
    }

    carregamentoInicialSemDados(): boolean {
        return this.carregandoAba() && this.enderecos().length === 0 && this.emails().length === 0;
    }

    private pessoaAtual(): Pessoa | null {
        const pessoaSelecionada = this.pessoa?.() ?? null;

        if (pessoaSelecionada?.id && pessoaSelecionada.uf) {
            return pessoaSelecionada;
        }

        if (this.ufSelecionada()) {
            return null;
        }

        return this.fallbackPessoa();
    }

    private ufSelecionada(): string | null {
        const uf = (this.uf() || '').trim().toUpperCase();
        return uf || null;
    }

    private async sincronizarPermissaoECarregarContatos(): Promise<void> {
        await this.atualizarPermissaoEscritaContato();
        await this.carregarContatos();
    }

    private obterUfPessoaPesquisada(): string | null {
        const pessoaAtual = this.pessoaAtualParaEscrita() ?? this.pessoaAtual();
        const ufPessoa = (pessoaAtual?.uf || '').trim().toUpperCase();
        return ufPessoa || null;
    }

    private async atualizarPermissaoEscritaContato(): Promise<void> {
        const cicloAtual = ++this.cicloPermissaoEscritaContato;

        if (!this.contextoRotaRegistroService.ehRotaContatosMedico()) {
            this.permissaoEscritaContato.set(true);
            return;
        }

        const ufPessoaPesquisada = this.obterUfPessoaPesquisada();
        if (!ufPessoaPesquisada) {
            this.permissaoEscritaContato.set(false);
            return;
        }

        this.permissaoEscritaContato.set(false);
        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: ufPessoaPesquisada } }));

            if (cicloAtual !== this.cicloPermissaoEscritaContato) {
                return;
            }

            this.permissaoEscritaContato.set(!!(resultado.validacoes['mesmaUf'] ?? false));
        } catch {
            if (cicloAtual !== this.cicloPermissaoEscritaContato) {
                return;
            }

            this.permissaoEscritaContato.set(false);
        }
    }

    private podeGerenciarContatos(): boolean {
        return !this.modoSomenteLeitura() && this.permissaoEscritaContato();
    }

    async podeAlterarEndereco(uf: string): Promise<boolean> {
        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: uf } }));
            return resultado.validacoes['mesmaUf'] ?? false;
        } catch {
            return false;
        }
    }

    private pessoaAtualParaEscrita(): Pessoa | null {
        const contextoSolicitanteOrgaoNormalizado = this.normalizarContextoSolicitanteOrgaoParaEscrita(this.pessoa?.());
        if (contextoSolicitanteOrgaoNormalizado) {
            return contextoSolicitanteOrgaoNormalizado;
        }

        const pessoaSessaoNormalizada = this.normalizarContextoSolicitanteOrgaoParaEscrita(this.fallbackPessoa());
        if (pessoaSessaoNormalizada) {
            return pessoaSessaoNormalizada;
        }

        return null;
    }

    private normalizarContextoSolicitanteOrgaoParaEscrita(origem: unknown): Pessoa | null {
        if (!origem || typeof origem !== 'object') {
            return null;
        }

        const candidato = origem as Record<string, unknown>;
        const idRaw = candidato['idSolicitante'] ?? candidato['id'];
        const ufRaw = candidato['ufSolicitante'] ?? candidato['sgUfSolicitante'] ?? candidato['uf'] ?? candidato['sgUf'] ?? this.ufSelecionada();

        const id = Number(idRaw);
        const uf = typeof ufRaw === 'string' ? ufRaw.trim().toUpperCase() : '';

        if (!Number.isFinite(id) || id <= 0 || !uf) {
            return null;
        }

        return { id, uf };
    }

    private limparContatos(): void {
        this.telefones.set([]);
        this.enderecos.set([]);
        this.emails.set([]);
        this.loadingEndereco = false;
        this.loadingEmail = false;
    }

    private carregarMunicipios(): void {
        this.municipioService.listarMunicipios().subscribe({
            next: (res) => {
                this.municipios.set(res || []);
            },
            error: (err) => {
                console.error('Erro ao carregar municípios', err);
            }
        });
    }

    initEnderecoForm(): void {
        this.enderecoForm.get('cep')?.valueChanges.subscribe((cep) => {
            this.buscarEnderecoPorCep(cep);
        });
    }

    initEditarEnderecoForm(): void {
        this.editarEnderecoForm.get('cep')?.valueChanges.subscribe((cep) => {
            this.buscarEnderecoPorCep(cep, true);
        });
    }

    initEmailForm(): void {}

    private async carregarContatos() {
        this.limparContatos();
        const p = this.pessoaAtual();
        if (!p || !p.id || !p.uf) {
            const uf = this.ufSelecionada();
            if (!uf) {
                return;
            }

            this.carregarContatosOrgao(uf);
            return;
        }

        // Emails
        this.carregarEmails(p);

        // Telefones
        await this.carregarTelefones(p);

        // Endereços
        this.carregarEndereco(p);
    }

    private carregarEndereco(p: Pessoa) {
        this.loadingEndereco = true;
        this.contatoService
            .listarEnderecos(p.id, p.uf)
            .pipe(finalize(() => (this.loadingEndereco = false)))
            .subscribe({
                next: (res) => {
                    this.enderecos.set(this.mapearEnderecos(res || [], this.telefones()));
                },
                error: (err) => {
                    console.error('Erro ao carregar endereços', err);
                    this.enderecos.set([]);
                }
            });
    }

    private async carregarTelefones(p: Pessoa) {
        try {
            const res = await firstValueFrom(this.contatoService.listarTelefones(p.id, p.uf));
            this.telefones.set(this.mapearTelefones(res || []));
        } catch (err) {
            console.error('Erro ao carregar telefones', err);
            this.telefones.set([]);
        }
    }

    private carregarEmails(p: Pessoa) {
        this.loadingEmail = true;
        this.contatoService
            .listarEmails(p.id, p.uf)
            .pipe(finalize(() => (this.loadingEmail = false)))
            .subscribe({
                next: (res) => {
                    this.emails.set(this.mapearEmails(res || []));
                },
                error: (err) => {
                    console.error('Erro ao carregar emails', err);
                    this.emails.set([]);
                }
            });
    }

    private carregarContatosOrgao(uf: string): void {
        this.loadingEndereco = true;
        this.loadingEmail = true;

        this.contatoService
            .listarContatosOrgaoPorUf(uf)
            .pipe(
                finalize(() => {
                    this.loadingEndereco = false;
                    this.loadingEmail = false;
                })
            )
            .subscribe({
                next: (res) => {
                    const telefones = this.mapearTelefones(res.telefones || []);
                    this.telefones.set(telefones);
                    this.emails.set(this.mapearEmails(res.emails || []));
                    this.enderecos.set(this.mapearEnderecos(res.enderecos || [], telefones));
                },
                error: (err) => {
                    console.error('Erro ao carregar contatos do orgao', err);
                    this.limparContatos();
                }
            });
    }

    private mapearTelefones(telefones: TelefoneResponse[]): TelefoneRow[] {
        return telefones.map((t) => {
            const dddOrigem = t.ddd ?? t.nuCodigoDdd ?? t.nuDdd;
            const telefoneOrigem = t.telefone ?? t.nuTelefone ?? t.numero;
            const tipoOrigem = t.tipo ?? t.inTipoTelefone;
            const { ddd, telefone } = this.normalizarDddTelefone(dddOrigem, telefoneOrigem);

            return {
                idEndereco: t.idEndereco || 0,
                ddd,
                telefone,
                tipo: this.normalizarTipoTelefone(tipoOrigem, telefone)
            };
        });
    }

    private mapearEmails(emails: EmailResponse[]): EmailRow[] {
        return emails.map((e) => ({
            email: e.email || '',
            tipo: e.tipo as TipoEmail
        }));
    }

    private mapearEnderecos(enderecos: EnderecoResponse[], telefones: TelefoneRow[]): EnderecoRow[] {
        return enderecos.map((r) => {
            const telefonesRelacionados = r.telefones ?? [];

            return {
                id: r.id || 0,
                logradouro: r.logradouro || '',
                bairro: r.bairro || '',
                municipio: r.municipio || '',
                uf: r.sgUf || '',
                cep: this.formatarCep(r.cep),
                tipo: r.tipo as EnderecoTipo,
                telefones:
                    telefonesRelacionados.length > 0
                        ? telefonesRelacionados.map((telefone) => this.formatarTelefoneTexto(telefone))
                        : telefones.filter((t) => t.idEndereco === r.id).map((tel) => this.formatarTelefoneComTipo((tel.ddd || '').toString(), (tel.telefone || '').toString(), tel.tipo))
            };
        });
    }

    buscarEnderecoPorCep(cep: string, isEdit = false): void {
        const cepLimpo = (cep || '').replace(/\D/g, '');
        if (cepLimpo.length === 8) {
            this.localidadesService.get(cepLimpo).subscribe({
                next: (dados) => {
                    if (dados) {
                        const municipioNome = (dados.municipio || '').toLowerCase();
                        const ufSigla = (dados.uf || '').toUpperCase();
                        const municipioObj = this.municipios().find((m) => m.dsMunicipio.toLowerCase() === municipioNome && m.uf.toUpperCase() === ufSigla);

                        const form = isEdit ? this.editarEnderecoForm : this.enderecoForm;
                        form.patchValue({
                            logradouro: dados.logradouro,
                            bairro: dados.bairro,
                            municipio: municipioObj ? municipioObj.id : null,
                            uf: dados.uf,
                            complemento: dados.complemento
                        });
                    }
                },
                error: (err) => {
                    console.error('Erro ao buscar CEP', err);
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível buscar o endereço por CEP. Preencha os campos manualmente.'
                    });
                }
            });
        }
    }

    showEnderecoDialog() {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        this.enderecoForm.reset({ tipo: 'R' });
        this.displayEnderecoDialog = true;
    }

    async salvarEndereco() {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        if (this.enderecoForm.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha todos os campos obrigatórios' });
            return;
        }

        const uf = this.ufSelecionada();
        if (uf && !(await this.podeAlterarEndereco(uf))) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const p = this.pessoaAtualParaEscrita();
        if (!p) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const enderecoValores = this.enderecoForm.getRawValue();
        const municipioId = enderecoValores.municipio;
        const municipioObj = this.municipios().find((m) => m.id === municipioId);

        const bairroTruncado = (this.enderecoForm.value.bairro || '').substring(0, 30);
        const novoEndereco = {
            ufSolicitante: p.uf,
            idSolicitante: p.id,
            ...enderecoValores,
            logradouro: enderecoValores.logradouro + ', ' + enderecoValores.numero + (enderecoValores.complemento ? ', ' + enderecoValores.complemento : ''),
            bairro: bairroTruncado,
            idMunicipio: municipioObj?.id,
            cep: enderecoValores.cep.replace(/\D/g, '')
        };

        const requisicaoSalvarEndereco = this.usarEndpointsDelegacia() ? this.contatoService.salvarEnderecoDelegacia(novoEndereco) : this.contatoService.salvarEndereco(novoEndereco);

        requisicaoSalvarEndereco.subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Endereço salvo com sucesso' });
                this.displayEnderecoDialog = false;
                this.carregarContatos();
                this.emitirDadosAtualizados();
            },
            error: (err) => {
                console.error('Erro ao salvar endereço', err);
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao salvar endereço' });
            }
        });
    }

    abrirEditarEndereco(addr: EnderecoRow): void {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        this.enderecoSelecionadoId = addr.id;

        const municipioObj = this.municipios().find((m) => m.dsMunicipio === addr.municipio && m.uf === addr.uf);

        // Extrair número e complemento do logradouro se possível
        // O formato salvo parece ser "Logradouro, Numero, Complemento" ou "Logradouro, Numero"
        const partes = addr.logradouro.split(',').map((p) => p.trim());
        const logradouroBase = partes[0] || '';
        const numero = partes[1] || '';
        const complemento = partes[2] || '';

        this.editarEnderecoForm.patchValue(
            {
                id: addr.id,
                cep: addr.cep,
                logradouro: logradouroBase,
                numero: numero,
                complemento: complemento,
                bairro: addr.bairro,
                municipio: municipioObj ? municipioObj.id : null,
                uf: addr.uf,
                tipo: addr.tipo
            },
            { emitEvent: false }
        );
        this.telefonesFA.clear();
        this.editingTelefones.set([]); // Reset editing state
        this.isEditEnderecoVisible.set(false);

        // Carregar telefones existentes
        const existingPhones = this.telefones().filter((t) => t.idEndereco === addr.id);
        existingPhones.forEach((t) => {
            const telefoneNormalizado = this.normalizarDddTelefone(t.ddd, t.telefone);
            const dddNormalizado = telefoneNormalizado.ddd;
            const numeroNormalizado = telefoneNormalizado.telefone;
            const tipoNormalizado = this.normalizarTipoTelefone(t.tipo, numeroNormalizado);
            const numeroFormatado = this.formatarNumeroTelefonePorTipo(numeroNormalizado, tipoNormalizado);
            const grupo = this.fb.group({
                ddd: [dddNormalizado, [Validators.required]],
                numero: [numeroFormatado, [Validators.required]],
                tipo: [tipoNormalizado, [Validators.required]],
                isNew: [false],
                originalDdd: [dddNormalizado],
                originalNumero: [numeroNormalizado],
                originalTipo: [tipoNormalizado]
            });
            this.telefonesFA.push(grupo);
        });

        this.displayEditarEnderecoDialog = true;
    }

    async salvarEdicaoEndereco(): Promise<void> {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        if (this.enderecoSemTelefonesInvalido || !this.podeSalvarTelefones()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha todos os campos obrigatórios' });
            return;
        }

        const uf = this.ufSelecionada();
        if (uf && !(await this.podeAlterarEndereco(uf))) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const p = this.pessoaAtualParaEscrita();
        if (!p) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const municipioId = this.editarEnderecoForm.value.municipio;
        const municipioObj = this.municipios().find((m) => m.id === municipioId);
        const bairroTruncado = (this.editarEnderecoForm.value.bairro || '').substring(0, 30);
        const { telefones: _telefones, ...dadosEnderecoForm } = this.editarEnderecoForm.getRawValue();

        const enderecoAtualizado = {
            ufSolicitante: p.uf,
            idSolicitante: p.id,
            ...dadosEnderecoForm,
            logradouro: dadosEnderecoForm.logradouro.trim() + ', ' + dadosEnderecoForm.numero + (dadosEnderecoForm.complemento ? ', ' + dadosEnderecoForm.complemento.trim() : ''),
            bairro: bairroTruncado,
            idMunicipio: municipioObj?.id,
            cep: dadosEnderecoForm.cep.replace(/\D/g, '')
        };

        const requisicaoAtualizarEndereco = this.usarEndpointsDelegacia() ? this.contatoService.atualizarEnderecoDelegacia(enderecoAtualizado) : this.contatoService.atualizarEndereco(enderecoAtualizado);

        requisicaoAtualizarEndereco.subscribe({
            next: () => {
                this.emitirDadosAtualizados();
                this.salvarTelefones(true, false);
            },
            error: (err) => {
                console.error('Erro ao atualizar endereço', err);
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.obterMensagemErroAtualizacao(err, 'endereço') });
            }
        });
    }

    get telefonesFA(): FormArray {
        return this.editarEnderecoForm.get('telefones') as FormArray;
    }

    // Controle de edição de telefones
    editingTelefones = signal<number[]>([]);

    isTelefoneEditing(index: number): boolean {
        return this.editingTelefones().includes(index) || this.telefonesFA.at(index).get('isNew')?.value === true;
    }

    toggleTelefoneEdit(index: number, editing: boolean): void {
        const current = this.editingTelefones();
        if (editing) {
            this.formatarTelefoneParaEdicao(index);
            this.editingTelefones.set([...current, index]);
        } else {
            const group = this.telefonesFA.at(index) as FormGroup | undefined;
            if (group?.get('isNew')?.value === true) {
                group.get('isNew')?.setValue(false);
            }
            this.editingTelefones.set(current.filter((i) => i !== index));
        }
    }

    getTelefoneMask(index: number): string {
        const ctrl = this.telefonesFA.at(index);
        const tipo = this.obterTipoTelefoneDoFormulario(ctrl as FormGroup);
        return tipo === 'F' ? '9999-9999' : '99999-9999';
    }

    getTelefonePlaceholder(index: number): string {
        const ctrl = this.telefonesFA.at(index);
        const tipo = this.obterTipoTelefoneDoFormulario(ctrl as FormGroup);
        return tipo === 'F' ? '0000-0000' : '00000-0000';
    }

    ajustarMascaraTelefonePorTipo(index: number): void {
        const ctrl = this.telefonesFA.at(index);
        const numeroControl = ctrl.get('numero');
        const tipo = this.obterTipoTelefoneDoFormulario(ctrl as FormGroup);
        const valorAtual = (numeroControl?.value || '').toString().replace(/\D/g, '');

        if (!numeroControl) {
            return;
        }

        if (tipo === 'F') {
            numeroControl.setValue(valorAtual.slice(0, 8), { emitEvent: false });
            return;
        }

        numeroControl.setValue(valorAtual.slice(0, 9), { emitEvent: false });
    }

    private formatarTelefoneParaEdicao(index: number): void {
        const ctrl = this.telefonesFA.at(index);
        const numeroControl = ctrl.get('numero');

        if (!numeroControl) {
            return;
        }

        const tipo = this.obterTipoTelefoneDoFormulario(ctrl as FormGroup);
        const numero = (numeroControl.value || '').toString().replace(/\D/g, '');
        const numeroFormatado = this.formatarNumeroTelefonePorTipo(numero, tipo);

        numeroControl.setValue(numeroFormatado, { emitEvent: false });
    }

    adicionarTelefoneRow(): void {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        const grupo = this.fb.group({
            ddd: ['', [Validators.required]],
            numero: ['', [Validators.required]],
            // Novo telefone já vem como Celular (tipo mais comum) em vez de Fixo.
            tipo: ['C', [Validators.required]],
            isNew: [true],
            originalDdd: [''],
            originalNumero: [''],
            originalTipo: ['C']
        });
        this.telefonesFA.push(grupo);
    }

    podeSalvarTelefones(): boolean {
        if (this.telefonesFA.length === 0) {
            return true;
        }

        return this.telefonesFA.controls.every((ctrl) => this.telefoneFormularioValido(ctrl as FormGroup));
    }

    get enderecoSemTelefonesInvalido(): boolean {
        const fields = ['cep', 'logradouro', 'numero', 'bairro', 'municipio', 'uf', 'tipo'];
        return fields.some((f) => this.editarEnderecoForm.get(f)?.invalid ?? false);
    }

    async salvarTelefones(fecharDialog: boolean = false, emitirAtualizacao: boolean = true): Promise<void> {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        const uf = this.ufSelecionada();
        if (uf && !(await this.podeAlterarEndereco(uf))) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const p = this.pessoaAtualParaEscrita();
        if (!p || !this.enderecoSelecionadoId) {
            return;
        }

        if (this.telefonesFA.length === 0) {
            if (fecharDialog) {
                this.displayEditarEnderecoDialog = false;
            }
            return;
        }

        const requisicoes = this.telefonesFA.controls
            .map((ctrl) => ctrl as FormGroup)
            .map((ctrl) => {
                const reqAtual = this.montarRequisicaoTelefone(ctrl, p, this.enderecoSelecionadoId!);
                const isNew = !!ctrl.get('isNew')?.value;

                if (isNew) {
                    return this.usarEndpointsDelegacia() ? this.contatoService.salvarTelefoneDelegacia(reqAtual) : this.contatoService.salvarTelefone(reqAtual);
                }

                if (!this.telefoneFoiAlterado(ctrl)) {
                    return null;
                }

                const reqExclusaoAnterior = this.montarRequisicaoTelefoneOriginal(ctrl, p, this.enderecoSelecionadoId!);
                const requisicaoExcluir = this.usarEndpointsDelegacia() ? this.contatoService.excluirTelefoneDelegacia(reqExclusaoAnterior) : this.contatoService.excluirTelefone(reqExclusaoAnterior);
                const requisicaoSalvar = this.usarEndpointsDelegacia() ? this.contatoService.salvarTelefoneDelegacia(reqAtual) : this.contatoService.salvarTelefone(reqAtual);
                return requisicaoExcluir.pipe(concatMap(() => requisicaoSalvar));
            })
            .filter((obs) => !!obs);

        if (requisicoes.length === 0) {
            if (fecharDialog) {
                this.displayEditarEnderecoDialog = false;
            }
            return;
        }

        forkJoin(requisicoes)
            .pipe(finalize(() => {}))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Telefones salvos com sucesso.' });
                    if (fecharDialog) {
                        this.displayEditarEnderecoDialog = false;
                    }
                    this.carregarContatos();
                    if (emitirAtualizacao) {
                        this.emitirDadosAtualizados();
                    }
                    // Limpar estado de edição
                    this.editingTelefones.set([]);
                },
                error: (err) => {
                    console.error('Erro ao salvar telefones', err);
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar os telefones.' });
                }
            });
    }

    removerTelefoneRow(index: number): void {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        const group = this.telefonesFA.at(index) as FormGroup;
        const isNew = group.get('isNew')?.value;
        const p = this.pessoaAtualParaEscrita();

        if (isNew) {
            this.telefonesFA.removeAt(index);
        } else {
            if (!p) {
                this.notificarContextoEscritaInvalido();
                return;
            }

            this.confirmationService.confirm({
                message: 'Deseja realmente excluir este telefone?',
                header: 'Excluir Telefone',
                icon: 'pi pi-trash',
                acceptLabel: 'Sim',
                rejectLabel: 'Não',
                accept: () => {
                    const ddd = (group.get('ddd')?.value || '').toString().replace(/\D/g, '');
                    const numero = (group.get('numero')?.value || '').toString().replace(/\D/g, '');
                    const tipo = group.get('tipo')?.value;

                    const req = {
                        ufSolicitante: p?.uf,
                        idSolicitante: p?.id,
                        idEndereco: this.enderecoSelecionadoId,
                        nuTelefone: numero,
                        nuCodigoDdd: ddd,
                        inTipoTelefone: tipo
                    };

                    const requisicaoExcluirTelefone = this.usarEndpointsDelegacia() ? this.contatoService.excluirTelefoneDelegacia(req) : this.contatoService.excluirTelefone(req);

                    requisicaoExcluirTelefone.subscribe({
                        next: () => {
                            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Telefone removido' });
                            this.telefonesFA.removeAt(index);
                            this.carregarContatos();
                            this.emitirDadosAtualizados();
                        },
                        error: (err) => {
                            console.error('Erro ao excluir telefone', err);
                            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao excluir telefone' });
                        }
                    });
                }
            });
        }
    }

    async confirmarExclusaoEndereco(addr: EnderecoRow) {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        const uf = this.ufSelecionada();
        if (uf && !(await this.podeAlterarEndereco(uf))) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const p = this.pessoaAtualParaEscrita();
        if (!p) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        this.confirmationService.confirm({
            message: 'Deseja realmente excluir este endereço?',
            header: 'Excluir Endereço',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                const requisicaoExcluirEndereco = this.usarEndpointsDelegacia() ? this.contatoService.excluirEnderecoDelegacia(p.id, p.uf, addr.id) : this.contatoService.excluirEndereco(p.id, p.uf, addr.id);

                requisicaoExcluirEndereco.subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Endereço removido' });
                        this.carregarContatos();
                        this.emitirDadosAtualizados();
                    },
                    error: (err) => {
                        console.error('Erro ao excluir endereço', err);
                        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao excluir endereço' });
                    }
                });
            }
        });
    }

    showEmailDialog(email?: EmailRow) {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        if (email) {
            this.emailOriginal = email.email;
            this.emailForm.patchValue({
                email: email.email,
                tipo: email.tipo
            });
        } else {
            this.emailOriginal = null;
            this.emailForm.reset({ tipo: 'P' });
        }
        this.displayEmailDialog = true;
    }

    /**
     * Validação de campo do formulário de e-mail. Escopado a `emailForm` — chamar com um campo de
     * `enderecoForm`/`editarEnderecoForm` devolve `false` silenciosamente.
     */
    campoInvalido(nomeCampo: string): boolean {
        const campo = this.emailForm.get(nomeCampo);
        if (!campo) return false;

        return campo.invalid && (campo.touched || campo.dirty);
    }

    /**
     * Mensagem de erro de campo do formulário de e-mail. Escopado a `emailForm` — chamar com um campo de
     * `enderecoForm`/`editarEnderecoForm` devolve `null` silenciosamente.
     */
    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.emailForm.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['email']) return 'E-mail inválido.';
        return 'Valor inválido.';
    }

    /** Validação de um campo do formulário de endereço (adicionar ou editar). */
    campoEnderecoInvalido(form: FormGroup, nomeCampo: string): boolean {
        const campo = form.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    /** Mensagem de erro de um campo do formulário de endereço (adicionar ou editar). */
    mensagemErroEndereco(form: FormGroup, nomeCampo: string): string | null {
        const campo = form.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['pattern']) return 'Informe apenas números.';
        if (campo.errors['ufInvalida']) return 'UF inválida.';
        if (campo.errors['maxlength']) return `Máximo de ${campo.errors['maxlength'].requiredLength} caracteres.`;
        return 'Valor inválido.';
    }

    salvarEmail() {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        if (this.emailForm.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha todos os campos corretamente' });
            return;
        }

        const p = this.pessoaAtualParaEscrita();

        if (!p || !p.id || !p.uf) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        const novoEmail = this.emailForm.get('email')?.value;
        const tipo = this.emailForm.get('tipo')?.value;

        if (!this.usarEndpointsDelegacia() && this.emailOriginal && this.emailOriginal !== novoEmail) {
            const reqExclusao = {
                ufSolicitante: p.uf,
                idSolicitante: p.id,
                email: this.emailOriginal
            };
            this.contatoService.excluirEmail(reqExclusao).subscribe({
                error: (err) => console.error('Erro ao excluir email anterior', err)
            });
        }

        const req = {
            ufSolicitante: p.uf,
            idSolicitante: p.id,
            email: novoEmail,
            tipo: tipo
        };

        const requisicaoSalvarEmail = this.emailOriginal
            ? this.usarEndpointsDelegacia()
                ? this.contatoService.atualizarEmailDelegacia(req)
                : this.contatoService.salvarEmail(req)
            : this.usarEndpointsDelegacia()
              ? this.contatoService.salvarEmailDelegacia(req)
              : this.contatoService.salvarEmail(req);

        requisicaoSalvarEmail.subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'E-mail salvo com sucesso' });
                this.displayEmailDialog = false;
                this.emailOriginal = null;
                this.carregarContatos();
                this.emitirDadosAtualizados();
            },
            error: (err) => {
                console.error('Erro ao salvar email', err);
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.obterMensagemErroAtualizacao(err, 'e-mail') });
            }
        });
    }

    confirmarExclusaoEmail(email: EmailRow) {
        if (!this.podeGerenciarContatos()) {
            return;
        }

        const p = this.pessoaAtualParaEscrita();
        if (!p) {
            this.notificarContextoEscritaInvalido();
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente excluir o e-mail ${email.email}?`,
            header: 'Excluir E-mail',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                const req = {
                    ufSolicitante: p.uf,
                    idSolicitante: p.id,
                    email: email.email
                };
                const requisicaoExcluirEmail = this.usarEndpointsDelegacia() ? this.contatoService.excluirEmailDelegacia(req) : this.contatoService.excluirEmail(req);

                requisicaoExcluirEmail.subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'E-mail removido' });
                        this.carregarContatos();
                        this.emitirDadosAtualizados();
                    },
                    error: (err) => {
                        console.error('Erro ao excluir email', err);
                        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao excluir email' });
                    }
                });
            }
        });
    }

    private emitirDadosAtualizados(): void {
        this.dadosAtualizados.emit();
    }

    private obterMensagemErroAtualizacao(erro: unknown, entidade: string): string {
        const mensagemErro = erro instanceof Error ? erro.message : '';
        if (/404|not found|nao encontrado|não encontrado/i.test(mensagemErro.toLowerCase())) {
            return `Não foi possível atualizar o ${entidade}, pois ele não foi encontrado. Atualize a tela e tente novamente.`;
        }

        return `Erro ao atualizar ${entidade}`;
    }

    private notificarContextoEscritaInvalido(): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Não foi possível identificar o contexto solicitante do órgão para salvar alterações nesta aba.'
        });
    }

    private formatarCep(cep?: string): string {
        const c = (cep || '').replace(/\D/g, '');
        if (c.length === 8) {
            return `${c.slice(0, 5)}-${c.slice(5)}`;
        }
        return c;
    }

    formatarNumeroTelefonePorTipo(numero: string, tipo: string): string {
        const numeroLimpo = (numero || '').toString().replace(/\D/g, '');
        const tipoNormalizado = this.normalizarTipoTelefone(tipo, numeroLimpo);

        if (tipoNormalizado === TipoTelefone.FIXO) {
            if (numeroLimpo.length < 8) {
                return numeroLimpo;
            }
            return `${numeroLimpo.slice(0, 4)}-${numeroLimpo.slice(4, 8)}`;
        }

        if (numeroLimpo.length < 9) {
            return numeroLimpo;
        }
        return `${numeroLimpo.slice(0, 5)}-${numeroLimpo.slice(5, 9)}`;
    }

    private normalizarTipoTelefone(tipo: unknown, telefone?: string): TipoTelefone {
        const tipoNormalizado = (tipo || '').toString().trim().toUpperCase();
        const numero = (telefone || '').toString().replace(/\D/g, '');

        if (numero.length === 8) {
            return TipoTelefone.FIXO;
        }

        if (numero.length === 9) {
            return TipoTelefone.CELULAR;
        }

        if (tipoNormalizado === TipoTelefone.FIXO || tipoNormalizado === TipoTelefone.CELULAR) {
            return tipoNormalizado as TipoTelefone;
        }

        return TipoTelefone.CELULAR;
    }

    private obterTipoTelefoneDoFormulario(ctrl: FormGroup): TipoTelefone {
        const tipo = (ctrl.get('tipo')?.value || '').toString().trim().toUpperCase();

        if (tipo === TipoTelefone.FIXO || tipo === TipoTelefone.CELULAR) {
            return tipo as TipoTelefone;
        }

        return this.normalizarTipoTelefone(tipo, ctrl.get('numero')?.value);
    }

    private normalizarDddTelefone(dddOrigem: unknown, telefoneOrigem: unknown): { ddd: string; telefone: string } {
        let ddd = (dddOrigem || '').toString().replace(/\D/g, '').slice(0, 2);
        let telefone = (telefoneOrigem || '').toString().replace(/\D/g, '');

        if (!ddd && telefone.length >= 10) {
            ddd = telefone.slice(0, 2);
            telefone = telefone.slice(2);
        } else if (ddd && telefone.length > 9) {
            telefone = telefone.startsWith(ddd) ? telefone.slice(2) : telefone.slice(-9);
        }

        if (telefone.length > 9) {
            telefone = telefone.slice(-9);
        }

        return { ddd, telefone };
    }

    private telefoneFormularioValido(ctrl: FormGroup): boolean {
        const ddd = (ctrl.get('ddd')?.value || '').toString().replace(/\D/g, '');
        const numero = (ctrl.get('numero')?.value || '').toString().replace(/\D/g, '');
        const tipo = this.obterTipoTelefoneDoFormulario(ctrl);

        if (ddd.length !== 2) {
            return false;
        }

        if (tipo === TipoTelefone.FIXO) {
            return numero.length === 8;
        }

        return numero.length === 9;
    }

    private formatarTelefoneTexto(valor: string): string {
        const texto = (valor || '').toString().trim();
        if (!texto) {
            return '';
        }

        const partesMultiplas = texto
            .split(/[;/|]+/)
            .map((item) => item.trim())
            .filter(Boolean);
        if (partesMultiplas.length > 1) {
            return partesMultiplas.map((parte) => this.formatarTelefoneTexto(parte)).join(' / ');
        }

        let tipoInformado: TipoTelefone | null = null;
        let textoSemTipo = texto;

        const prefixoTipo = texto.match(/^\s*(celular|fixo|c|f)\s*[:-]?\s*(.*)$/i);
        if (prefixoTipo) {
            const tipoBruto = (prefixoTipo[1] || '').toUpperCase();
            tipoInformado = tipoBruto === 'F' || tipoBruto === 'FIXO' ? TipoTelefone.FIXO : TipoTelefone.CELULAR;
            textoSemTipo = (prefixoTipo[2] || '').trim();
        }

        const match = textoSemTipo.match(/\((\d{2})\)\s*([0-9-]+)/);
        if (match) {
            const ddd = match[1] || '';
            const numero = (match[2] || '').replace(/\D/g, '');
            const tipo = tipoInformado || (numero.length === 8 ? TipoTelefone.FIXO : TipoTelefone.CELULAR);
            return this.formatarTelefoneComTipo(ddd, numero, tipo);
        }

        let digitos = textoSemTipo.replace(/\D/g, '');
        if (digitos.length > 11 && digitos.startsWith('55')) {
            digitos = digitos.slice(2);
        }

        if (digitos.length >= 10) {
            const ddd = digitos.slice(0, 2);
            const numero = digitos.slice(2, 11);
            const tipo = tipoInformado || (numero.length === 8 ? TipoTelefone.FIXO : TipoTelefone.CELULAR);
            return this.formatarTelefoneComTipo(ddd, numero, tipo);
        }

        if (digitos.length === 8 || digitos.length === 9) {
            const tipo = tipoInformado || (digitos.length === 8 ? TipoTelefone.FIXO : TipoTelefone.CELULAR);
            const numeroFormatado = this.formatarNumeroTelefonePorTipo(digitos, tipo);
            const descricaoTipo = tipo === TipoTelefone.FIXO ? 'Fixo' : 'Celular';
            return `${descricaoTipo} ${numeroFormatado}`;
        }

        return texto;
    }

    private formatarTelefoneComTipo(ddd: string, numero: string, tipo: string): string {
        const tipoNormalizado = this.normalizarTipoTelefone(tipo, numero);
        const descricaoTipo = tipoNormalizado === TipoTelefone.FIXO ? 'Fixo' : 'Celular';
        const telefone = this.formatarTelefoneComMascara(ddd, numero, tipoNormalizado);
        return `${descricaoTipo} ${telefone}`.trim();
    }

    private formatarTelefoneComMascara(ddd: string, numero: string, tipo: string): string {
        const dddLimpo = (ddd || '').toString().replace(/\D/g, '').slice(0, 2);
        const numeroLimpo = (numero || '').toString().replace(/\D/g, '');
        const numeroFormatado = this.formatarNumeroTelefonePorTipo(numeroLimpo, tipo);
        if (!dddLimpo) {
            return numeroFormatado;
        }

        return MascaraUtil.telefone(`${dddLimpo}${numeroLimpo}`);
    }

    private montarRequisicaoTelefone(ctrl: FormGroup, p: Pessoa, idEndereco: number): any {
        const ddd = (ctrl.get('ddd')?.value || '').toString().replace(/\D/g, '');
        const numero = (ctrl.get('numero')?.value || '').toString().replace(/\D/g, '');
        const tipo = this.obterTipoTelefoneDoFormulario(ctrl);

        return {
            ufSolicitante: p.uf,
            idSolicitante: p.id,
            idEndereco,
            nuCodigoDdd: ddd,
            nuTelefone: numero,
            inTipoTelefone: tipo
        };
    }

    private montarRequisicaoTelefoneOriginal(ctrl: FormGroup, p: Pessoa, idEndereco: number): any {
        const ddd = (ctrl.get('originalDdd')?.value || '').toString().replace(/\D/g, '');
        const numero = (ctrl.get('originalNumero')?.value || '').toString().replace(/\D/g, '');
        const tipo = this.normalizarTipoTelefone(ctrl.get('originalTipo')?.value, numero);

        return {
            ufSolicitante: p.uf,
            idSolicitante: p.id,
            idEndereco,
            nuCodigoDdd: ddd,
            nuTelefone: numero,
            inTipoTelefone: tipo
        };
    }

    private telefoneFoiAlterado(ctrl: FormGroup): boolean {
        const dddAtual = (ctrl.get('ddd')?.value || '').toString().replace(/\D/g, '');
        const numeroAtual = (ctrl.get('numero')?.value || '').toString().replace(/\D/g, '');
        const tipoAtual = this.obterTipoTelefoneDoFormulario(ctrl);

        const dddOriginal = (ctrl.get('originalDdd')?.value || '').toString().replace(/\D/g, '');
        const numeroOriginal = (ctrl.get('originalNumero')?.value || '').toString().replace(/\D/g, '');
        const tipoOriginal = this.normalizarTipoTelefone(ctrl.get('originalTipo')?.value, numeroOriginal);

        return dddAtual !== dddOriginal || numeroAtual !== numeroOriginal || tipoAtual !== tipoOriginal;
    }
}
